import { useEffect, useMemo, useCallback } from 'react';
import { RealtimeKitProvider, useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { AlertCircle, Calendar } from 'lucide-react';
import MeetingRoom from './MeetingRoom';
import { RtkUiProvider } from '@cloudflare/realtimekit-react-ui';

function MeetingArea() {
  const [meeting, initMeeting] = useRealtimeKitClient();

  const authToken = useMemo(() => {
    const searchParams = new URL(window.location.href).searchParams;
    return searchParams.get('authToken');
  }, []);

  const handleMeetingError = useCallback((err: Error) => {
    console.error('Failed to initialize meeting:', err);
    alert('Failed to join meeting. The authentication token may be invalid or expired.');
  }, []);

  useEffect(() => {
    if (!authToken) {
      return;
    }

    initMeeting({
      authToken,
      defaults: {
        video: false,
        audio: false,
      },
    })
      .then(m => {
        if (m) {
          m.joinRoom();
        }
      })
      .catch(handleMeetingError);
  }, [authToken, initMeeting, handleMeetingError]);

  if (!authToken) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="container py-16">
          <div className="mx-auto max-w-2xl">
            <div className="card text-center">
              <div className="mb-6 flex justify-center">
                <div className="rounded-full bg-primary-100 p-4 dark:bg-primary-950">
                  <Calendar className="text-primary-600 dark:text-primary-400" size={48} />
                </div>
              </div>

              <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-50">Meet Me</h1>
              <p className="mb-6 text-lg text-gray-600 dark:text-gray-400">
                Schedule a video call with me using Cloudflare RealtimeKit
              </p>

              <div className="rounded-lg bg-primary-50 p-6 text-left dark:bg-primary-950 dark:ring-1 dark:ring-primary-900">
                <div className="flex gap-3">
                  <AlertCircle className="flex-shrink-0 text-primary-600 dark:text-primary-400" size={20} />
                  <div>
                    <h3 className="mb-2 font-semibold text-primary-900 dark:text-primary-100">
                      Scheduling Feature Coming Soon
                    </h3>
                    <p className="text-sm text-primary-800 dark:text-primary-300">
                      The meeting scheduling feature is currently under development. In the
                      meantime, you can join a meeting if you have a valid authentication token.
                    </p>
                    <p className="mt-3 text-sm text-primary-800 dark:text-primary-300">
                      To join a meeting, use a URL like:{' '}
                      <code className="rounded bg-primary-100 px-2 py-1 dark:bg-primary-900 dark:text-primary-200">
                        rathor.dev/meet-me?authToken=YOUR_TOKEN
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RealtimeKitProvider
      value={meeting}
      fallback={
        <div className="w-full h-[500px] p-4 flex items-center justify-center">
          <p className="text-2xl">Loading...</p>
        </div>
      }
    >
      <RtkUiProvider meeting={meeting} showSetupScreen={true}>
        <MeetingRoom />
      </RtkUiProvider>
    </RealtimeKitProvider>
  );
}

export default MeetingArea;
